<template>
  <v-menu v-model="showMenu" :close-on-content-click="false" content-class="shell-pop ntf-pop" left max-width="420"
          nudge-bottom="8" offset-y transition="slide-y-transition">
    <template v-slot:activator="{on, attrs}">
      <button :aria-label="unreadCount ? `Benachrichtigungen, ${unreadCount} ungelesen` : 'Benachrichtigungen'"
              :class="{ 'shell-ib--on': showMenu }" v-tip="'Benachrichtigungen'" class="shell-ib" type="button" v-bind="attrs" v-on="on">
        <v-icon>mdi-bell-outline</v-icon>
        <span v-if="unreadCount >= 1" class="shell-count">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
      </button>
    </template>
    <div class="ntf">
      <div class="ntf__head">
        <div class="ntf__title">Benachrichtigungen</div>
        <button :disabled="!unreadCount && !loadedUnread.length" class="cta cta--quiet ntf__head-btn"
                title="Alle als gelesen markieren" type="button" @click="markAllRead">
          <v-icon class="cta__icon" small>mdi-check-all</v-icon>
          Alle gelesen
        </button>
        <button v-tip="muted ? 'Systemtöne einschalten' : 'Systemtöne ausschalten'"
                :aria-label="muted ? 'Systemtöne einschalten' : 'Systemtöne ausschalten'" class="shell-ib ntf__mute" type="button"
                @click="toggleMute">
          <v-icon small>{{ muted ? 'mdi-volume-off' : 'mdi-volume-high' }}</v-icon>
        </button>
        <button aria-label="Einstellungen für Benachrichtigungen" v-tip="'Einstellungen'" class="shell-ib ntf__mute" type="button"
                @click="openSettings">
          <v-icon small>mdi-cog-outline</v-icon>
        </button>
        <button v-if="$vuetify.breakpoint.xsOnly" v-tip="'Schließen'" aria-label="Schließen" class="shell-ib" type="button"
                @click="showMenu = false">
          <v-icon small>mdi-close</v-icon>
        </button>
      </div>
      <div class="ntf__seg">
        <segmented-control v-model="segment" :items="segmentItems" block label="Benachrichtigungen filtern"/>
      </div>
      <div v-if="segment === 'dismissed'" class="ntf__note">
        <v-icon x-small>mdi-information-outline</v-icon>
        Endgültige Löschung nach 30 Tagen
      </div>
      <div class="ntf__list" @scroll.passive="onListScroll">
        <v-skeleton-loader v-if="loading && !notifications.length" type="list-item-avatar-two-line@3"/>
        <template v-else-if="groups.length">
          <section v-for="group in groups" :key="group.key" class="ntf__group">
            <div class="ntf__day">{{ group.label }}</div>
            <notification-row v-for="entry in group.entries" :key="String(entry.notification._id)" :busy="busyKey === entryKey(entry)"
                              :entry="entry" :now="now" @complete="complete" @dismiss="dismiss" @dismiss-group="dismissGroup"
                              @mark-unread="markUnread" @navigate="showMenu = false" @revoke="revoke" @snooze="snooze"
                              @snooze-to-do="snoozeToDo"/>
          </section>
        </template>
        <div v-else class="ntf__empty">
          <v-icon>{{ empty.icon }}</v-icon>
          <div class="ntf__empty-title">{{ empty.title }}</div>
          <div class="ntf__empty-text">{{ empty.text }}</div>
        </div>
      </div>
      <div class="ntf__foot">
        <button v-if="segment === 'open' && openTotal > 1" class="cta cta--ghost ntf__foot-btn" type="button"
                @click="dismissAll">
          <v-icon class="cta__icon" small>mdi-delete-sweep-outline</v-icon>
          Alle in den Papierkorb
        </button>
        <button v-if="segment === 'dismissed' && dismissedNotifications.length > 1" class="cta cta--ghost ntf__foot-btn" type="button"
                @click="revokeAll">
          <v-icon class="cta__icon" small>mdi-delete-restore</v-icon>
          Alle wiederherstellen
        </button>
        <span class="ntf__grow"/>
        <button v-if="canLoadMore" :disabled="loading" class="cta cta--ghost ntf__foot-btn" type="button" @click="loadMore">
          Mehr laden
        </button>
      </div>
    </div>
    <complete-to-do-item-dialog ref="completeDialog"/>
  </v-menu>
</template>
<script lang="ts">
import Vue from 'vue';
import {NotifierApi, NotificationCounts} from '@/classes/api/notifier.api';
import {NotificationLike} from '@/types/NotificationLike.type';
import {NotificationBus} from '@/busses/NotificationBus';
import mongoose from 'mongoose';
import {NotifierEvents} from '@/enums/notifier/NotifierEvents';
import {SoundManager} from '@/classes/clientOnly/SoundManager.class';
import SegmentedControl, {SegmentItem} from '@/components/common/modern/segmentedControl.vue';
import CompleteToDoItemDialog from '@/components/toDos/completeToDoItemDialog.vue';
import NotificationRow from './notificationRow.vue';
import {
  filterSegment,
  groupEntriesByDay,
  NotificationEntryGroup,
  NotificationSegment,
  sortForSegment,
  toDoIdOf,
} from './notificationView.helper';
import {collapseRepeated, NotificationEntry} from '@/helpers/notificationGroups.helper';
import {
  completeFromNotification,
  NotificationSnoozePreset,
  snoozeNotifications,
  snoozeText,
  snoozeToDoFromNotification,
} from '@/components/app/appBar/notifications/notificationActions.helper';
import {offerUndoCompletion, ToDoSnoozePreset} from '@/components/toDos/toDoActions.helper';

type Id = mongoose.Types.ObjectId;

const EMPTY: Record<NotificationSegment, { icon: string; title: string; text: string }> = {
  open: {icon: 'mdi-island', title: 'Keine offenen Benachrichtigungen', text: 'Hier werden deine Benachrichtigungen angezeigt.'},
  mentions: {icon: 'mdi-at', title: 'Keine Erwähnungen', text: 'Hier erscheint, wenn dich jemand in einem Kommentar mit @ erwähnt.'},
  dismissed: {icon: 'mdi-delete-empty-outline', title: 'Papierkorb ist leer', text: 'Hier werden gelöschte Benachrichtigungen angezeigt.'},
};

/** Page size of the panel (server-side paging) */
const PAGE_SIZE = 30;

/** Server filter per segment */
const SEGMENT_FILTER: Record<NotificationSegment, { dismissed: boolean; type?: string[] }> = {
  open: {dismissed: false},
  mentions: {dismissed: false, type: ['mention']},
  dismissed: {dismissed: true},
};

const idsOf = (entry: NotificationEntry): Id[] => entry.members.map((el) => el._id);

/**
 * Bell in the app bar with the notification panel (segments Offen / Erwähnungen / Papierkorb, grouped by day).
 * Notify v2: server counts, mark unread, bulk restore, snooze and group dismissal when the notifier supports them;
 * on an old notifier the panel works as before (local counts, one restore call per notification).
 */
export default Vue.extend({
  components: {CompleteToDoItemDialog, NotificationRow, SegmentedControl},
  data: () => ({
    showMenu: false,
    /** Server paging per segment: newest first, PAGE_SIZE per page (users can have thousands of notifications) */
    page: 1,
    maxPage: 1,
    /** Increased per list request: an answer for an older request (e.g. segment switched meanwhile) is ignored */
    requestSeq: 0,
    loading: false,
    notifications: [] as NotificationLike[],
    segment: 'open' as NotificationSegment,
    unreadCount: 0,
    /** Counts of the new notifier (null: old notifier, counts of the loaded notifications are shown) */
    serverCounts: null as NotificationCounts | null,
    countTimer: 0,
    busyKey: '',
    /** Marked as unread by the user in this session: not auto-read when the panel closes */
    keepUnread: [] as string[],
    muted: false,
    /** Current time for the relative times, updated every minute while the panel is open */
    now: Date.now(),
    clock: 0,
  }),
  computed: {
    openNotifications(): NotificationLike[] {
      return filterSegment(this.notifications, 'open');
    },
    mentionNotifications(): NotificationLike[] {
      return filterSegment(this.notifications, 'mentions');
    },
    dismissedNotifications(): NotificationLike[] {
      return filterSegment(this.notifications, 'dismissed');
    },
    segmentItems(): SegmentItem[] {
      const count = (n: number) => (n > 0 ? n : undefined);
      const server = this.serverCounts;
      return [
        {value: 'open', text: 'Offen', count: count(server ? server.open : this.openNotifications.length)},
        {value: 'mentions', text: 'Erwähnungen', count: count(server ? server.mentions : this.mentionNotifications.length)},
        {value: 'dismissed', text: 'Papierkorb', count: count(server ? server.trash : this.dismissedNotifications.length)},
      ];
    },
    entries(): NotificationEntry[] {
      return collapseRepeated(sortForSegment(filterSegment(this.notifications, this.segment)));
    },
    groups(): NotificationEntryGroup[] {
      return groupEntriesByDay(this.entries);
    },
    loadedUnread(): NotificationLike[] {
      return this.notifications.filter((el) => !el.readAt);
    },
    /** More pages on the server for the current segment */
    canLoadMore(): boolean {
      return this.page < this.maxPage;
    },
    /** Open notifications in total (server count; old notifier: loaded ones) */
    openTotal(): number {
      return this.serverCounts ? this.serverCounts.open : this.openNotifications.length;
    },
    empty(): { icon: string; title: string; text: string } {
      return EMPTY[this.segment];
    },
  },
  methods: {
    entryKey(entry: NotificationEntry): string {
      return String(entry.notification._id);
    },
    async initialize() {
      this.loading = true;
      try {
        await this.refreshCounts();
      } catch (e) {
        this.$$showSnackbar('Fehler beim Laden der Benachrichtigungen', 'error', e);
      } finally {
        this.loading = false;
      }
    },
    /** Server counts on a new notifier, otherwise the old unread count */
    async refreshCounts() {
      const counts = await NotifierApi.getCounts();
      if (counts) {
        this.serverCounts = counts;
        this.unreadCount = counts.unread;
        return;
      }
      this.serverCounts = null;
      this.unreadCount = await NotifierApi.getUnreadCount();
    },
    /** After changes: new counts from the server (debounced; old notifier keeps counting locally) */
    scheduleCountRefresh() {
      if (!this.serverCounts) {
        return;
      }
      window.clearTimeout(this.countTimer);
      this.countTimer = window.setTimeout(() => this.refreshCounts().catch(() => undefined), 800);
    },
    loadMore() {
      if (this.loading || !this.canLoadMore) {
        return;
      }
      this.loadNotifications(this.page + 1);
    },
    /** Infinite scroll: the next page near the end of the list */
    onListScroll(event: Event) {
      const el = event.target as HTMLElement;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) {
        this.loadMore();
      }
    },
    toggleMute() {
      if (this.muted) {
        SoundManager.unmute();
      } else {
        SoundManager.mute();
      }
      this.muted = SoundManager.isMuted;
    },
    openSettings() {
      this.showMenu = false;
      NotificationBus.$emit('openSettings');
    },
    /** Page `page` of the current segment from the server (page 1 replaces the list, further pages are appended) */
    async loadNotifications(page = 1) {
      const segment = this.segment;
      const seq = ++this.requestSeq;
      this.loading = true;
      try {
        const resp = await NotifierApi.getOwnNotifications({
          page,
          limit: PAGE_SIZE,
          sortBy: 'createdAt',
          sortDesc: true,
          markDelivered: true,
          filter: SEGMENT_FILTER[segment] as never,
        });
        if (seq !== this.requestSeq) {
          return;
        }
        const loaded = resp.notifications.map((el) => ({...el, showInApp: false}) as NotificationLike);
        if (page === 1) {
          this.notifications = loaded;
        } else {
          const known = new Set(this.notifications.map((el) => String(el._id)));
          this.notifications = [...this.notifications, ...loaded.filter((el) => !known.has(String(el._id)))];
        }
        this.page = page;
        this.maxPage = resp.maxPage || 1;
      } catch (e) {
        this.$$showSnackbar('Fehler beim Laden der Benachrichtigungen', 'error', e);
      } finally {
        if (seq === this.requestSeq) {
          this.loading = false;
        }
      }
    },
    setDismissed(ids: Id[], dismissed: boolean) {
      const keys = ids.map(String);
      this.notifications.forEach((el) => {
        if (!keys.includes(String(el._id))) {
          return;
        }
        if (dismissed) {
          this.$set(el, 'dismissedAt', new Date());
          this.$store.commit('hideNotification', el._id);
        } else {
          this.$delete(el, 'dismissedAt');
        }
      });
      this.scheduleCountRefresh();
    },
    /** Moves notifications to the trash at once and offers undo (restore) */
    async trash(ids: Id[], text: string, dismissCall: () => Promise<void>) {
      const affected = this.notifications.filter((el) => ids.map(String).includes(String(el._id)));
      await dismissCall();
      this.setDismissed(ids, true);
      await this.bulkRead(affected.filter((el) => !el.readAt));
      this.$$showUndo({
        text,
        undo: async () => {
          await NotifierApi.revokeDismissions(ids);
          this.setDismissed(ids, false);
        },
      });
    },
    async dismiss(entry: NotificationEntry) {
      const ids = idsOf(entry);
      try {
        await this.trash(ids, ids.length > 1 ? `${ids.length} Benachrichtigungen im Papierkorb` : 'In den Papierkorb verschoben',
          () => NotifierApi.dismiss({notificationIds: ids}));
      } catch (e) {
        this.$$showSnackbar('Fehler beim Löschen der Benachrichtigung', 'error', e);
      }
    },
    /** "Alle in den Papierkorb": one server update for all open notifications (not only the loaded page) */
    async dismissAll() {
      const ids = this.openNotifications.map((el) => el._id);
      try {
        const result = await NotifierApi.dismissAll({});
        if (!result) {
          // old notifier: only the loaded ones
          await this.trash(ids, `${ids.length} Benachrichtigungen im Papierkorb`, () => NotifierApi.dismiss({notificationIds: ids}));
          return;
        }
        this.setDismissed(ids, true);
        this.refreshCounts().catch(() => undefined);
        this.$$showUndo({
          text: `${result.count} Benachrichtigungen im Papierkorb`,
          undo: async () => {
            await NotifierApi.revokeAllDismissions(result.dismissedAt);
            this.loadNotifications();
            this.refreshCounts().catch(() => undefined);
          },
        });
      } catch (e) {
        this.$$showSnackbar('Fehler beim Löschen der Benachrichtigungen', 'error', e);
      }
    },
    /** "Alle 14 entfernen": the whole group (also not loaded ones on a new notifier) */
    async dismissGroup(entry: NotificationEntry) {
      const ids = idsOf(entry);
      const legacyIds = entry.members.filter((el) => !el.groupKey).map((el) => el._id);
      this.busyKey = this.entryKey(entry);
      try {
        await this.trash(ids, `${ids.length} Meldungen im Papierkorb`,
          () => (entry.groupKey ? NotifierApi.dismissGroup(entry.groupKey, ids, legacyIds) : NotifierApi.dismiss({notificationIds: ids})));
      } catch (e) {
        this.$$showSnackbar('Fehler beim Löschen der Benachrichtigungen', 'error', e);
      } finally {
        this.busyKey = '';
      }
    },
    async revoke(entry: NotificationEntry) {
      const ids = idsOf(entry);
      try {
        await NotifierApi.revokeDismissions(ids);
        this.setDismissed(ids, false);
      } catch (e) {
        this.$$showSnackbar('Fehler beim Wiederherstellen der Benachrichtigung', 'error', e);
      }
    },
    /** "Alle wiederherstellen" in the trash (one call on a new notifier) */
    async revokeAll() {
      const ids = this.dismissedNotifications.map((el) => el._id);
      try {
        await NotifierApi.revokeDismissions(ids);
        this.setDismissed(ids, false);
        this.$$showUndo({
          text: `${ids.length} Benachrichtigungen wiederhergestellt`,
          undo: async () => {
            await NotifierApi.dismiss({notificationIds: ids});
            this.setDismissed(ids, true);
          },
        });
      } catch (e) {
        this.$$showSnackbar('Fehler beim Wiederherstellen der Benachrichtigungen', 'error', e);
      }
    },
    async markUnread(entry: NotificationEntry) {
      const ids = idsOf(entry);
      if (!(await NotifierApi.markUnread(ids))) {
        this.$$showSnackbar('Als ungelesen markieren ist gerade nicht möglich', 'error');
        return;
      }
      const keys = ids.map(String);
      this.keepUnread = Array.from(new Set([...this.keepUnread, ...keys]));
      this.notifications.forEach((el) => {
        if (keys.includes(String(el._id)) && el.readAt) {
          this.$delete(el, 'readAt');
          this.unreadCount++;
        }
      });
      this.scheduleCountRefresh();
    },
    async snooze(entry: NotificationEntry, preset: NotificationSnoozePreset) {
      const ids = idsOf(entry);
      if (!(await snoozeNotifications(ids, preset))) {
        this.$$showSnackbar('Später erinnern ist gerade nicht möglich', 'error');
        return;
      }
      const keys = ids.map(String);
      this.notifications = this.notifications.filter((el) => !keys.includes(String(el._id)));
      this.$$showSnackbar(snoozeText(preset), 'success');
      this.scheduleCountRefresh();
    },
    /** Notifications of the same to-do are read once the to-do was handled */
    readToDo(toDoId?: string) {
      const related = this.notifications.filter((el) => toDoId && toDoIdOf(el) === toDoId && !el.readAt);
      this.bulkRead(related).then(() => this.applyRead(related.map((el) => el._id))).catch(() => undefined);
    },
    async complete(notification: NotificationLike) {
      this.busyKey = String(notification._id);
      try {
        const {result, toDoId} = await completeFromNotification(notification);
        if (result === 'needsDialog' && toDoId) {
          await this.completeWithDialog(toDoId);
        } else if (result === 'alreadyDone') {
          this.$$showSnackbar('Das To-do ist bereits erledigt', 'info');
        } else if (result === 'missing') {
          this.$$showSnackbar('Das To-do gibt es nicht mehr', 'info');
        }
        this.readToDo(toDoId);
      } catch (e) {
        this.$$showSnackbar('Das To-do konnte nicht erledigt werden', 'error', e);
      } finally {
        this.busyKey = '';
      }
    },
    async completeWithDialog(toDoId: string) {
      const dialog = this.$refs.completeDialog as Vue & { open: (id: Id) => Promise<unknown> };
      try {
        await dialog.open(toDoId as unknown as Id);
        offerUndoCompletion({_id: toDoId});
      } catch (e) {
        // dialog closed without completing
      }
    },
    async snoozeToDo(notification: NotificationLike, preset: ToDoSnoozePreset) {
      this.busyKey = String(notification._id);
      try {
        if (!(await snoozeToDoFromNotification(notification, preset))) {
          this.$$showSnackbar('Das To-do ist bereits erledigt oder gelöscht', 'info');
        }
        this.readToDo(toDoIdOf(notification));
      } catch (e) {
        this.$$showSnackbar('Die Fälligkeit konnte nicht verschoben werden', 'error', e);
      } finally {
        this.busyKey = '';
      }
    },
    async bulkRead(notifications: NotificationLike[]) {
      const ids = notifications.filter((el) => !this.keepUnread.includes(String(el._id))).map((el) => el._id);
      if (ids.length > 0) {
        await NotifierApi.markRead({notificationIds: ids});
      }
    },
    /** "Alle gelesen": one server update for all open unread notifications (old notifier: the loaded ones) */
    async markAllRead() {
      const unread = this.loadedUnread;
      this.keepUnread = [];
      try {
        const result = await NotifierApi.markAllRead({});
        if (!result) {
          await this.bulkRead(unread);
        }
        this.applyRead(unread.map((el) => el._id));
        this.refreshCounts().catch(() => undefined);
      } catch (e) {
        this.$$showSnackbar('Fehler beim Markieren der Benachrichtigungen als gelesen', 'error', e);
      }
    },
    /** Local state after a read (own call or socket event); notifications that are already read are skipped */
    applyRead(ids: Id[]) {
      ids.forEach((id) => {
        const index = this.notifications.findIndex((el) => String(el._id) === String(id) && !el.readAt);
        if (index >= 0) {
          this.$set(this.notifications[index], 'readAt', new Date());
          this.unreadCount = Math.max(0, this.unreadCount - 1);
          this.$store.commit('hideNotification', id);
        }
      });
      this.scheduleCountRefresh();
    },
    startClock() {
      this.now = Date.now();
      window.clearInterval(this.clock);
      this.clock = window.setInterval(() => {
        this.now = Date.now();
      }, 60000);
    },
    onPush(notification: NotificationLike) {
      const index = this.notifications.findIndex((el) => String(el._id) === String(notification._id));
      if (index >= 0) {
        // snoozed notification delivered again: unread at the top
        this.notifications.splice(index, 1);
      } else if (!notification.readAt) {
        this.unreadCount++;
      }
      this.notifications.unshift(notification);
      this.scheduleCountRefresh();
    },
    onUpdate() {
      this.loadNotifications();
      this.refreshCounts().catch(() => undefined);
    },
    onMarkRead(ids: Id[]) {
      this.$nextTick(() => this.applyRead(ids));
    },
    onDismiss(ids: Id[]) {
      this.setDismissed(ids, true);
    },
    onRevoke(ids: Id[]) {
      this.setDismissed(ids, false);
    },
    onOpenPanel() {
      this.showMenu = true;
    },
  },
  watch: {
    /** Leaving a segment marks its unread notifications as read (as the former tabs did) */
    segment(value: NotificationSegment, previous: NotificationSegment) {
      const unread = filterSegment(this.notifications, previous).filter((el) => !el.readAt);
      this.bulkRead(unread).catch(() => undefined);
      // every segment is paged on the server
      this.notifications = [];
      this.page = 1;
      this.maxPage = 1;
      if (this.showMenu) {
        this.loadNotifications();
      } else {
        this.requestSeq++;
        this.loading = false;
      }
    },
    showMenu(v: boolean) {
      if (v) {
        this.startClock();
        if (this.notifications.length === 0) {
          this.loadNotifications();
        }
        // via the mutation: the other tabs hide these pop-ups too (opening the panel counts as seen)
        this.$store.state.rtcNotifications
          .filter((el: NotificationLike) => el.showInApp)
          .forEach((el: NotificationLike) => this.$store.commit('hideNotification', el._id));
      } else {
        window.clearInterval(this.clock);
        this.$nextTick(async () => {
          const unreadNotifications = this.notifications.filter((el) => !el.readAt);
          await this.bulkRead(unreadNotifications);
          this.segment = 'open';
        });
      }
    },
  },
  mounted() {
    this.initialize();
    this.muted = SoundManager.isMuted;
    NotificationBus.$on('pushRtc', this.onPush);
    NotificationBus.$on('update', this.onUpdate);
    NotificationBus.$on('openPanel', this.onOpenPanel);
    NotificationBus.$on(NotifierEvents.MARK_READ, this.onMarkRead);
    NotificationBus.$on(NotifierEvents.DISMISS, this.onDismiss);
    NotificationBus.$on(NotifierEvents.REVOKE_DISMISSION, this.onRevoke);
  },
  beforeDestroy() {
    window.clearInterval(this.clock);
    window.clearTimeout(this.countTimer);
    NotificationBus.$off('pushRtc', this.onPush);
    NotificationBus.$off('update', this.onUpdate);
    NotificationBus.$off('openPanel', this.onOpenPanel);
    NotificationBus.$off(NotifierEvents.MARK_READ, this.onMarkRead);
    NotificationBus.$off(NotifierEvents.DISMISS, this.onDismiss);
    NotificationBus.$off(NotifierEvents.REVOKE_DISMISSION, this.onRevoke);
  },
});
</script>

<style>
.ntf-pop.v-menu__content {
  width: 400px;
  max-height: min(640px, calc(100vh - 72px)) !important;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.ntf {
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-height: inherit;
  color: var(--sh-ink);
}

.ntf__head {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 10px 8px 16px;
}

.ntf__title {
  flex: 1 1 auto;
  font-size: 14px;
  font-weight: 600;
}

.ntf .ntf__head-btn {
  height: 30px;
  padding: 0 10px;
  font-size: 12px;
}

.ntf__mute.shell-ib {
  width: 32px;
  height: 32px;
}

.ntf__seg {
  padding: 0 16px 8px;
}

.ntf__note {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 16px 6px;
  padding: 6px 10px;
  border-radius: 8px;
  background: var(--sh-warn-soft);
  color: var(--sh-warn);
  font-size: 12px;
}

.ntf__note .v-icon {
  color: inherit !important;
}

.ntf__list {
  flex: 1 1 auto;
  min-height: 80px;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding-bottom: 6px;
  border-top: 1px solid var(--sh-line);
}

.ntf__day {
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 10px 16px 4px;
  background: var(--sh-panel);
  color: var(--sh-muted);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: .04em;
  text-transform: uppercase;
}

.ntf__empty {
  padding: 36px 24px;
  text-align: center;
  color: var(--sh-muted);
  font-size: 13px;
}

.ntf__empty .v-icon {
  color: inherit !important;
  margin-bottom: 6px;
}

.ntf__empty-title {
  color: var(--sh-ink);
  font-weight: 600;
}

.ntf__foot {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-top: 1px solid var(--sh-line);
}

.ntf__foot:empty, .ntf__foot:not(:has(button)) {
  display: none;
}

.ntf__grow {
  flex: 1 1 auto;
}

.ntf .ntf__foot-btn {
  height: 30px;
  padding: 0 10px;
  font-size: 12px;
}

/* Phone: full-width sheet below the app bar */
@media (max-width: 599px) {
  .ntf-pop.v-menu__content {
    left: 0 !important;
    right: 0 !important;
    top: 52px !important;
    width: 100vw;
    max-width: 100vw !important;
    min-width: 100vw !important;
    max-height: calc(100vh - 52px) !important;
    max-height: calc(100dvh - 52px) !important;
    border-radius: 0 0 16px 16px !important;
    border-left: 0;
    border-right: 0;
  }
}
</style>
